import { z } from 'zod'

import { recordPath, recordRoute } from '@/shared/registry'

import { classApi, classPath, classRoute } from '../class-form/class'
import { enrollmentStatuses } from '../roster/roster'

/** A class's pre-class checklist: beside Class Details, reached from its side menu. */
export const checklistRoute = `${classRoute}/checklist`

export function checklistPath(classId: string): string {
  return `${classPath(classId)}/checklist`
}

/** An organization's materials responsibilities, reached from Organization Details' side menu; `classId` keeps one class's. */
export const materialsResponsibilitiesRoute = recordRoute('classes', 'materials-responsibilities')

export function materialsResponsibilitiesPath(organizationId: string, classId?: string): string {
  const path = recordPath('classes', 'materials-responsibilities', organizationId)
  return classId === undefined ? path : `${path}?${new URLSearchParams({ classId }).toString()}`
}

/** Who supplies a student's materials, in the order the checklist offers them. */
export const materialsSuppliers = ['studentOrganization', 'classOrganization', 'noOne'] as const
export type MaterialsSupplier = (typeof materialsSuppliers)[number]

/** The checklist's steps, by the API's names, in the order the checklist shows them. */
export const checklistItems = ['confirmationCall', 'materialsSent', 'materialsReceived', 'connectivityVerified'] as const
export type ChecklistItem = (typeof checklistItems)[number]

/** Each step's segment in the API's path. */
export const checklistItemPaths: Readonly<Record<ChecklistItem, string>> = {
  confirmationCall: 'confirmation-call',
  materialsSent: 'materials-sent',
  materialsReceived: 'materials-received',
  connectivityVerified: 'connectivity-verified',
}

/** Where a class's materials list is read and saved; the save shares the class's version. */
export function classMaterialsListApi(classId: string): string {
  return `${classApi(classId)}/materials-list`
}

export function checklistApi(classId: string): string {
  return `${classApi(classId)}/checklist`
}

/** Marks a step done (PUT) or clears it (DELETE). */
export function checklistStepApi(classId: string, enrollmentId: string, item: ChecklistItem): string {
  return `${checklistApi(classId)}/${encodeURIComponent(enrollmentId)}/${checklistItemPaths[item]}`
}

export function materialsSupplierApi(classId: string, enrollmentId: string): string {
  return `${checklistApi(classId)}/${encodeURIComponent(enrollmentId)}/materials-supplier`
}

/** The API's materials responsibility search, with its filters. */
export function materialsResponsibilitiesApi(organizationId: string, filters: Readonly<Record<string, string>>): string {
  return `/classes/v1/materials-responsibilities?${new URLSearchParams({ organizationId, ...filters }).toString()}`
}

const choiceSchema = z.object({ id: z.string(), name: z.string(), isDeleted: z.boolean() })
export type MaterialsListChoice = z.infer<typeof choiceSchema>

const classMaterialsListSchema = z.object({
  classId: z.string(),
  /** The saved course, whose lists these are. */
  courseId: z.string(),
  materialsList: choiceSchema.nullish(),
  choices: z.array(choiceSchema),
  mayChange: z.boolean(),
})

/** A class's materials list and the lists of its course it may take. */
export type ClassMaterialsList = z.infer<typeof classMaterialsListSchema>

export function parseClassMaterialsList(body: unknown): ClassMaterialsList {
  return classMaterialsListSchema.parse(body)
}

const studentSchema = z.object({
  enrollmentId: z.string(),
  userProfileId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  status: z.enum(enrollmentStatuses),
  classroomName: z.string().nullish(),
  materialsSupplier: z.enum(materialsSuppliers),
  confirmationCallOn: z.string().nullish(),
  materialsSentOn: z.string().nullish(),
  materialsReceivedOn: z.string().nullish(),
  connectivityVerifiedOn: z.string().nullish(),
})
export type ChecklistStudent = z.infer<typeof studentSchema>

const checklistSchema = z.object({
  classId: z.string(),
  className: z.string(),
  startsOn: z.string(),
  timeZone: z.string(),
  materialsListName: z.string().nullish(),
  showsConnectivity: z.boolean(),
  mayRecord: z.boolean(),
  students: z.array(studentSchema),
})

/** A class's pre-class checklist. */
export type Checklist = z.infer<typeof checklistSchema>

export function parseChecklist(body: unknown): Checklist {
  return checklistSchema.parse(body)
}

const stepSchema = z.object({ enrollmentId: z.string(), doneOn: z.string().nullish() })

export function parseChecklistStep(body: unknown): z.infer<typeof stepSchema> {
  return stepSchema.parse(body)
}

/** When a student's step was done, or `null`. */
export function doneOn(student: ChecklistStudent, item: ChecklistItem): string | null {
  return student[`${item}On`] ?? null
}

const responsibilitySchema = z.object({
  enrollmentId: z.string(),
  userProfileId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  /** Whether the caller may read the student's profile; without it, no email, phone or address comes. */
  showsContact: z.boolean(),
  address: z.object({
    line1: z.string().nullish(),
    line2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
  }).nullish(),
  studentOrganizationName: z.string().nullish(),
  materialsSupplier: z.enum(materialsSuppliers),
  status: z.enum(enrollmentStatuses),
  classId: z.string(),
  className: z.string(),
  classOrganizationName: z.string().nullish(),
  startsOn: z.string(),
  timeZone: z.string(),
  materialsSentOn: z.string().nullish(),
  materialsReceivedOn: z.string().nullish(),
  materials: z.array(z.object({ id: z.string(), title: z.string(), productNumber: z.string().nullish(), isbn: z.string().nullish(), vendorName: z.string() })),
})

/** A student whose materials the organization supplies. */
export type MaterialsResponsibility = z.infer<typeof responsibilitySchema>

export function parseMaterialsResponsibility(item: unknown): MaterialsResponsibility {
  return responsibilitySchema.parse(item)
}

/** A postal address's lines, the empty ones left out. */
export function addressLines(address: NonNullable<MaterialsResponsibility['address']>): readonly string[] {
  const place = [address.city, address.state, address.postalCode].filter((part): part is string => part !== null && part !== undefined && part !== '').join(', ')
  return [address.line1, address.line2, place, address.country].filter((line): line is string => line !== null && line !== undefined && line !== '')
}
